// base/components/standalone/Layout/index.tsx
import { Main, MainProps } from '@/components/Main';
import { Navigation, NavigationProps } from '@/components/Navigation';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { StandaloneComponentProps } from '@/types/component';
import dynamic from 'next/dynamic';
import { ReactNode, Suspense, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';

const LazyFooter = dynamic(() => import('@/components/Footer').then((m) => m.Footer), {
  ssr: false,
  // @ts-expect-error: suspense is supported at runtime, but not in type defs
  suspense: true,
});

const DeferredFooter = (props: React.ComponentProps<typeof LazyFooter>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useIntersectionObserver(
    [ref],
    ([entry], obs) => {
      if (entry.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    },
    { rootMargin: '400px 0px' },
  );

  useEffectOnce(() => {
    if (window?.innerHeight > 4000) setVisible(true); // make footer visible when viewport is very tall (Googlebot)
  });

  // optional probe
  if (visible && !(window as any).__footerLoaded) {
    (window as any).__footerLoaded = Date.now();
    console.log('[footer] loaded');
  }

  return (
    <footer ref={ref}>
      {visible ? (
        <Suspense fallback={null}>
          <LazyFooter {...props} />
        </Suspense>
      ) : null}
    </footer>
  );
};

export interface StandaloneLayoutProps extends StandaloneComponentProps {
  content?: ReactNode;
  options?: {
    $navigation?: NavigationProps;
    $main?: MainProps;
    $footer?: React.ComponentProps<typeof LazyFooter>;
  };
}

export const StandaloneLayout = ({ content, options }: StandaloneLayoutProps) => {
  const { $navigation, $main, $footer } = { ...options };
  return (
    <>
      <Navigation {...$navigation} />
      <Main content={content} {...$main} />
      <DeferredFooter {...($footer as any)} />
    </>
  );
};
